import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ContractRepaymentsAnalytics from '@/components/contracts/ContractRepaymentsAnalytics';
import ContractRepaymentsTransactions from '@/components/contracts/ContractRepaymentsTransactions';
import ContractRepaymentsOutstanding from '@/components/contracts/ContractRepaymentsOutstanding';
import { ContractFilterSheet, ContractFilter } from '@/components/contracts/ContractFilterSheet';
import { Receipt } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';
import { UniversalDateRangePicker } from '@/components/UniversalDateRangePicker';
import { startOfWeek, endOfWeek, format, parse, isValid, subYears, endOfDay } from 'date-fns';
import { businessToday } from '@/lib/format';
import { driverProfileName } from '@/lib/driverProfile';

/**
 * The URL range, or null when the params are absent, incomplete, or not exactly `yyyy-MM-dd`.
 *
 * Local-midnight parse: new Date('yyyy-MM-dd') is UTC midnight, which re-serialises one day earlier
 * at negative UTC offsets and shifts the filter on every reload. `to` is pushed to end-of-day so a
 * reloaded range matches what the picker emits in-session (endOfWeek/endOfDay) — a bare midnight
 * `to` truncates the last day's expected total.
 *
 * parse() accepts only this exact mask, so a legacy or hand-edited param (an ISO datetime, say)
 * yields Invalid Date and the tabs would throw 'Invalid time value' mid-render. Both the state
 * initializer and handleTabChange read this one result, so a malformed param falls back to the tab
 * default in both places — testing raw param presence in handleTabChange instead left `?from=garbage`
 * pinning every tab to Summary's 1-year default.
 */
function parseUrlRange(urlFrom: string | null, urlTo: string | null): { from: Date; to: Date } | null {
    if (!urlFrom || !urlTo) return null;
    const from = parse(urlFrom, 'yyyy-MM-dd', new Date());
    const to = parse(urlTo, 'yyyy-MM-dd', new Date());
    if (!isValid(from) || !isValid(to)) return null;
    return { from, to: endOfDay(to) };
}

export default function ContractRepayments() {
    const { isLoading: authLoading } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlContractId = searchParams.get('contract_id');
    const urlFrom = searchParams.get('from');
    const urlTo = searchParams.get('to');


    // Fetch unassigned transactions count for badge
    const { data: unassignedCount } = useQuery({
        queryKey: ['unassigned-transactions-count'],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_unassigned_transactions_count');
            if (error) throw error;
            return data as number;
        },
        staleTime: 30000, // Cache for 30 seconds
    });

    // Active tab state
    const [activeTab, setActiveTab] = useState<string>('summary');

    // The validated URL range (null when absent or malformed), shared by the state initializer and
    // handleTabChange so both agree on whether the user has pinned a range.
    const urlRange = parseUrlRange(urlFrom, urlTo);

    // Date range state - Summary defaults to 1 year, others to current week
    const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(() => {
        if (urlRange) return urlRange;
        // Default to 1 year for Summary tab (initial tab)
        const now = businessToday();
        return {
            from: subYears(now, 1),
            to: endOfDay(now),
        };
    });

    // Handle tab change - update date range based on tab
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        // Only change date range if no usable URL range (user hasn't manually selected one)
        if (!urlRange) {
            const now = businessToday();
            if (tab === 'summary') {
                // Summary: 1 year range
                setDateRange({
                    from: subYears(now, 1),
                    to: endOfDay(now),
                });
            } else {
                // Transactions/Collections: current week
                setDateRange({
                    from: startOfWeek(now, { weekStartsOn: 1 }),
                    to: endOfWeek(now, { weekStartsOn: 1 }),
                });
            }
        }
    };

    const handleDateRangeChange = (newRange: { from: Date; to: Date }) => {
        setDateRange(newRange);
        const params = new URLSearchParams(searchParams);
        if (newRange?.from) params.set('from', format(newRange.from, 'yyyy-MM-dd'));
        if (newRange?.to) params.set('to', format(newRange.to, 'yyyy-MM-dd'));
        setSearchParams(params, { replace: true });
    };

    // Alias for compatibility with child components until refactored
    const monthRange = dateRange;

    // Initialize filter from URL immediately if present
    const initialContractId = urlContractId ? parseInt(urlContractId, 10) : null;
    const [contractFilter, setContractFilter] = useState<ContractFilter | null>(
        initialContractId && !isNaN(initialContractId)
            ? { contract_id: initialContractId, driver_name: 'Loading...' }
            : null
    );

    const handleFilterChange = (newFilter: ContractFilter | null) => {
        setContractFilter(newFilter);
        const params = new URLSearchParams(searchParams);
        if (newFilter) {
            params.set('contract_id', newFilter.contract_id.toString());
        } else {
            params.delete('contract_id');
        }
        setSearchParams(params);
    };

    // Fetch driver name for URL contract_id
    useEffect(() => {
        async function fetchDriverName() {
            if (urlContractId) {
                const contractId = parseInt(urlContractId, 10);
                if (!isNaN(contractId) && (contractFilter?.contract_id !== contractId || contractFilter?.driver_name === 'Loading...')) {
                    // Fetch driver name from database.
                    //
                    // `profiles.admin_id` IS SELECTED BECAUSE THE EMBED IS NOT ONE ROW.
                    // `profiles.driver_id` is unique only WHERE `admin_id IS NULL`, so an admin
                    // holding a test profile against this driver appears here beside them, and the
                    // badge would name the wrong person. `driverProfileName` applies the exclusion.
                    const { data } = await supabase
                        .from('contracts')
                        .select('drivers(profiles(id, admin_id, first_name, last_name))')
                        .eq('id', contractId)
                        .single();

                    const driverName = driverProfileName(data?.drivers?.profiles) ?? `Contract #${contractId}`;

                    setContractFilter({ contract_id: contractId, driver_name: driverName || `Contract #${contractId}` });
                }
            } else if (contractFilter !== null) {
                // If URL param is removed but state exists (e.g. back button), clear state
                // However, handleFilterChange handles explicit clearing. 
                // This handles browser navigation (back/forward) clearing the param.
                setContractFilter(null);
            }
        }
        if (!authLoading) {
            fetchDriverName();
        }
    }, [urlContractId, authLoading]); // Removed contractFilter from dependency to avoid loop, handled inside

    if (authLoading) {
        return (
            <div className="space-y-6 px-4 sm:px-6 lg:px-8 pt-6">
                <div className="mb-8">
                    <Skeleton className="h-8 w-64 mb-2" />
                    <Skeleton className="h-4 w-96" />
                </div>
                <div className="space-y-6">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <Receipt className="h-8 w-8" />
                        <h1 className="text-4xl font-semibold tracking-tight">Contract Repayments</h1>
                    </div>
                    <p className="text-muted-foreground">Track all contract repayment transactions.</p>
                </div>
            </div>

            <Tabs defaultValue="summary" value={activeTab} onValueChange={handleTabChange} className="space-y-10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <TabsList>
                            <TabsTrigger value="summary">Summary</TabsTrigger>
                            <TabsTrigger value="transactions">
                                Transactions
                            </TabsTrigger>
                            <TabsTrigger value="collections">Collections</TabsTrigger>
                        </TabsList>
                        <ContractFilterSheet
                            selectedFilter={contractFilter}
                            onFilterChange={handleFilterChange}
                        />
                        {contractFilter && (
                            <Badge variant="secondary" className="text-sm">
                                Filtering: {contractFilter.driver_name} (Contract #{contractFilter.contract_id})
                            </Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <UniversalDateRangePicker
                            date={dateRange}
                            onDateChange={handleDateRangeChange}
                            className="h-9"
                        />
                    </div>
                </div>

                <TabsContent value="transactions">
                    <ContractRepaymentsTransactions contractId={contractFilter?.contract_id} monthRange={monthRange} />
                </TabsContent>

                <TabsContent value="collections">
                    <ContractRepaymentsOutstanding contractId={contractFilter?.contract_id} monthRange={monthRange} />
                </TabsContent>

                <TabsContent value="summary">
                    <ContractRepaymentsAnalytics contractId={contractFilter?.contract_id} monthRange={monthRange} />
                </TabsContent>
            </Tabs>


        </div>
    );
}

